import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getEmbedParentOrigin,
  getScrubbedEmbedUrl,
  getEmbedAuthStateFromSearch,
  getExpiredEmbedAuthState,
  normalizeEmbedAllowedOrigins,
  type EmbedAuthState,
  EMBED_ACCESS_TOKEN_QUERY_PARAM,
} from './embed-auth.utils';
import { useEmbedRuntime } from '../providers';

const defaultAuthState: EmbedAuthState = {
  accessToken: null,
  allowedOrigins: [],
  embeddingOrigin: null,
  expiresAtMs: null,
  session: null,
  status: 'missing-token',
};

const EmbedAuthContext = createContext<EmbedAuthState>(defaultAuthState);

export function EmbedAuthContextProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: EmbedAuthState;
}) {
  return (
    <EmbedAuthContext.Provider value={value}>
      {children}
    </EmbedAuthContext.Provider>
  );
}

export function EmbedAuthProvider({ children }: { children: ReactNode }) {
  const { allowedOrigins, scrubAccessTokenFromUrl } = useEmbedRuntime();
  const normalizedAllowedOrigins = useMemo(
    () => normalizeEmbedAllowedOrigins(allowedOrigins),
    [allowedOrigins]
  );

  const [authState, setAuthState] = useState<EmbedAuthState>(() => {
    if (typeof window === 'undefined') return defaultAuthState;

    const ancestorOrigins = (
      window.location as Location & {
        ancestorOrigins?: DOMStringList;
      }
    ).ancestorOrigins;

    return getEmbedAuthStateFromSearch({
      allowedOrigins: normalizedAllowedOrigins,
      embeddingOrigin: getEmbedParentOrigin({
        ancestorOrigins,
        referrer: document.referrer,
      }),
      nowMs: Date.now(),
      search: window.location.search,
    });
  });

  useEffect(() => {
    if (!scrubAccessTokenFromUrl || typeof window === 'undefined') return;
    if (!window.location.search.includes(EMBED_ACCESS_TOKEN_QUERY_PARAM))
      return;

    const scrubbedUrl = getScrubbedEmbedUrl(window.location.href);
    if (scrubbedUrl === window.location.href) return;

    window.history.replaceState(
      window.history.state,
      document.title,
      scrubbedUrl
    );
  }, [scrubAccessTokenFromUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (authState.status !== 'ready' || authState.expiresAtMs === null) return;

    const remainingMs = authState.expiresAtMs - Date.now();

    if (remainingMs <= 0) {
      setAuthState((currentState) => {
        if (currentState.status !== 'ready') return currentState;

        return getExpiredEmbedAuthState(currentState);
      });
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setAuthState((currentState) => {
        if (currentState.status !== 'ready') return currentState;

        return getExpiredEmbedAuthState(currentState);
      });
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [authState.expiresAtMs, authState.status]);

  const value = useMemo(() => authState, [authState]);

  return (
    <EmbedAuthContextProvider value={value}>
      {children}
    </EmbedAuthContextProvider>
  );
}

export function useEmbedAuth() {
  return useContext(EmbedAuthContext);
}
